// src/components/pages/message/DropMessagePage.js
import React, { useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { Box, CircularProgress } from "@mui/material";
import ActionButton from "../../atoms/ActionButton";
import LocationSearchBox from "../../LocationSearchBox";
import useLocationSearch from "../../../hooks/useLocationSearch";
import useGeolocationMap from "../../../hooks/useGeolocationMap";
import { NAVBAR_HEIGHT, BOTTOM_ACTION_BUTTONS_POSITION, } from "../../../constants/constants";

import IconUserLocationMarker from "../../../assets/icons/icon-user-location-marker.svg";
import IconMessageDefault from "../../../assets/icons/message-default.svg";
import IconRecenter from "../../../assets/icons/icon-recenter.svg";
import IconBuryTimeCapsule from "../../../assets/icons/icon-bury-capsule.png";

// Google Maps JS API loader 
import { googleMapsLoader } from "../../../googleMaps";

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

let placesService = null;
let geocoder = null;
googleMapsLoader.load().then(() => {
  // once the script is loaded:
  const google = window.google;
  geocoder = new google.maps.Geocoder();
  placesService = new google.maps.places.PlacesService(
    document.createElement("div")
  );
});

export default function DropMessagePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const mapContainerRef = useRef(null);

  // 1️⃣ pick up any passed state
  const passed = location.state || {};
  const initialCoords = passed.initialCoords || {
    lng: -98.5795,
    lat: 39.8283,
    zoom: 16,
  };
  const initialAddress = passed.initialAddress || "";

  // 2️⃣ init map (disable geolocation override if we were passed coords)
  const {
    map,
    coords,
    geolocationLoaded,
    handleRecenter,
  } = useGeolocationMap(
    mapContainerRef,
    initialCoords,
    "mapbox://styles/jaimeboyaca/cm7f1viks00ad01qsa8cy3xdb",
    IconUserLocationMarker,
    true,
    [],
    Boolean(passed.initialCoords)
  );

  // 3️⃣ wire up Google-backed search/autocomplete
  const { searchQuery, setSearchQuery, suggestions } = useLocationSearch(
    initialAddress,
    coords
  );

  // 4️⃣ when the user selects a dropdown item, fetch its geometry
  const handleSelect = (prediction) => {
    if (!placesService || !map) return;
    placesService.getDetails(
      { placeId: prediction.place_id },
      (place, status) => {
        if (status === "OK" && place.geometry) {
          const { lat, lng } = place.geometry.location;
          map.flyTo({ center: [lng(), lat()], zoom: 15 });
          setSearchQuery(prediction.description);
        }
      }
    );
  };

  // 5️⃣ reverse‐geocode on map move to update the box
  useEffect(() => {
    if (!map || !geocoder) return;
    const onMoveEnd = () => {
      const center = map.getCenter();
      geocoder.geocode(
        { location: { lat: center.lat, lng: center.lng } },
        (results, status) => {
          if (status === "OK" && results[0]) {
            setSearchQuery(results[0].formatted_address);
          }
        }
      );
    };
    map.on("moveend", onMoveEnd);
    return () => map.off("moveend", onMoveEnd);
  }, [map, setSearchQuery]);

  // 6️⃣ confirm → navigate on bury
  const handleConfirmLocation = () => {
    if (!coords || !searchQuery) {
      console.warn("No location data to confirm");
      return;
    }
    navigate(
      `/timecapsule?lat=${coords.lat}&lng=${coords.lng}&address=${encodeURIComponent(
        searchQuery
      )}`
    );
  };

  // 7️⃣ loader until map+geolocation are ready
  if (!geolocationLoaded) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: `calc(100vh - ${NAVBAR_HEIGHT}px)`,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: `calc(100vh - ${NAVBAR_HEIGHT}px)`,
      }}
    >
      {/* ▶︎ Google‐powered autocomplete box */}
      <Box
        sx={{
          position: "absolute",
          top: 16,
          left: 16,
          right: 16,
          zIndex: 2,
          bgcolor: "white",
          borderRadius: "50px",
        }}
      >
        <LocationSearchBox
          searchQuery={searchQuery}
          suggestions={suggestions}
          onInputChange={(e, v) => setSearchQuery(v)}
          onSelect={handleSelect}
          textFieldProps={{
            placeholder: "Search or type an address",
            variant: "outlined",
            size: "small",
            sx: {
              "& .MuiOutlinedInput-root": {
                borderRadius: "50px",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: (theme) => theme.colors.blue,
                },
              },
              "& .MuiOutlinedInput-input": {
                padding: "8px 45px 8px 42px !important",
              },
            },
          }}
        />
      </Box>

      {/* ▶︎ The Map */}
      <Box
        ref={mapContainerRef}
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: `calc(100vh - ${NAVBAR_HEIGHT}px)`,
        }}
      />

      {/* ▶︎ Center “bury” icon */}
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 2,
        }}
      >
        <img
          src={IconBuryTimeCapsule}
          alt="Drop Location"
          style={{ width: 82, height: 46 }}
        />
      </Box>

      {/* ▶︎ Bottom action buttons */}
      <Box
        sx={{
          position: "fixed",
          bottom: `${BOTTOM_ACTION_BUTTONS_POSITION}px`,
          left: 16,
          right: 16,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          zIndex: 2,
        }}
      >
        <ActionButton
          icon={IconMessageDefault}
          text="Confirm Drop Location"
          onClick={handleConfirmLocation}
          sx={(theme) => ({
            backgroundColor: theme.colors.white,
            border: `1px solid ${theme.palette.primary.main}`,
            color: theme.palette.primary.main,
            pl: "10px",
            pt: "10px",
            pb: "10px",
            boxShadow: "none",
            textTransform: "none",
            flex: "none",
          })}
        />
        <ActionButton
          icon={IconRecenter}
          onClick={handleRecenter}
          sx={(theme) => ({
            flex: "none",
            width: 50,
            height: 50,
            minWidth: "50px",
            borderRadius: "50%",
            bgcolor: theme.palette.primary.main,
            boxShadow: "none",
            "&:hover": { boxShadow: "none" },
          })}
        />
      </Box>
    </Box>
  );
}
